import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTabParam } from '../../hooks/useTabParam.js';
import { useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '../Icon';
import { getContactCategoryTone } from '../../lib/contactCategoryTone.js';
import { Button } from '../ui/Button.js';
import { Chip } from '../ui/Chip.js';
import { DetailSection } from '../ui/DetailSection.js';
import { SectionHeaderAction } from '../ui/SectionHeaderAction.js';
import { SocialLinksRow } from '../ui/SocialLinksRow.js';
import { IconButton } from '../ui/IconButton.js';
import { RelatedModulesGrid } from '../related/RelatedModules.js';
import { Input } from '../ui/Input.js';
import { KVGrid } from '../ui/KVGrid.js';
import { KVRow } from '../ui/KVRow.js';
import { Pressable } from '../ui/Pressable.js';
import { Select } from '../ui/Select.js';
import { Tabs } from '../ui/Tabs.js';
import { ActionDropdown } from '../ui/Dropdown.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { PillGroup } from '../PillGroup.js';
import { useTopBarCtx } from '../../contexts/TopBarContext.js';
import { LogEntry, LogKV, type LogChannel } from '../ui/LogEntry.js';
import { Textarea } from '../ui/Textarea.js';
import { LoadingState } from '../ui/LoadingState.js';
import { EmptyState } from '../ui/EmptyState.js';
import { Pill, type PillTone } from '../PillGroup.js';
import { useReports } from '../../hooks/useReports.js';
import { parseServerDate } from '../../lib/date.js';
import {
  useContact, useUpdateContact, useDeleteContact, useMergeContacts,
  useContactTagAssignments, useAssignTag, useRemoveTag, useContactTagsList,
  useContactCategoriesList, useCreateContactCategory,
  useContactEmails, useContactPhones,
  useAddContactEmail, useRemoveContactEmail, useSetPrimaryContactEmail,
  useAddContactPhone, useRemoveContactPhone, useSetPrimaryContactPhone,
  useContactTimeline,
  type Contact, type ContactTimelineEvent,
} from '../../hooks/useContacts.js';
import { useDmReplyChannels, type DmReplyChannel } from '../../hooks/useDmReply.js';
import { useBriefCore, useUpdateBriefCore } from '../../hooks/useBriefCore.js';
import { usePropagation } from '../../hooks/useEmail.js';
import { PropagationModal } from '../PropagationModal.js';
import { ContactProfileView } from './ContactProfileView.js';
import { MergeContactSelector } from './MergeContactSelector.js';
import { DmReplyComposer } from './DmReplyComposer.js';

interface Props {
  id: number;
  onClose: () => void;
  onDeleted: () => void;
}

// ── Outreach tab — bitácora unificada ─────────────────────────────────────────
// Único timeline por contacto (projects, estimates, invoices, email, DM —
// contactTimeline.ts). Antes había dos servicios compitiendo (este +
// contactOutreachLog.ts, Billing Phase 2 Part E); el segundo se borró — ver
// REGLA de Eduardo: "un solo servicio de línea de tiempo, la pantalla consume
// ese y nada más". Filas con deepLink navegan al documento dueño (responder
// pasa allá); filas sin deepLink (email, DM, notas) no son clickeables.

type TimelineDirection = 'sent' | 'received' | 'event';

const TIMELINE_DIRECTION: Record<string, TimelineDirection> = {
  email_inbound: 'received', email_outbound: 'sent',
  dm_inbound: 'received', dm_outbound: 'sent',
  lead_outreach_draft: 'sent', lead_outreach_sent: 'sent', lead_reply: 'received',
  project_outreach_draft: 'sent', project_outreach_sent: 'sent', project_reply: 'received',
  project_revision: 'event', project_client_response: 'event',
  estimate_outreach_draft: 'sent', estimate_outreach_sent: 'sent', estimate_reply: 'received',
  invoice_outreach_draft: 'sent', invoice_outreach_sent: 'sent', invoice_reply: 'received',
  note_manual: 'event', pipeline_stage_change: 'event',
};

const DIRECTION_TONES: Record<TimelineDirection, PillTone> = {
  sent: 'cyan',
  received: 'success',
  event: 'amber',
};

// DMs consecutivos del mismo canal se agrupan en un bloque de conversación
// (header con ícono+plataforma, mensajes adentro, composer al pie) — el
// resto de la línea de tiempo sigue siendo filas sueltas, intercaladas en el
// mismo orden cronológico. Nunca se parte por plataforma: agrupar es solo
// visual, el timeline que llega del backend ya viene ordenado DESC y esa es
// la única fuente de orden real.
type TimelineItem =
  | { kind: 'event'; event: ContactTimelineEvent }
  | { kind: 'dmBlock'; channelId: number; platform: string; runs: ContactTimelineEvent[] };

function groupTimeline(events: ContactTimelineEvent[]): TimelineItem[] {
  const items: TimelineItem[] = [];
  let i = 0;
  while (i < events.length) {
    const e = events[i]!;
    if (e.type === 'dm_inbound' || e.type === 'dm_outbound') {
      const channelId = (e.metadata?.['channelId'] as number | undefined) ?? -1;
      const platform = (e.metadata?.['platform'] as string | undefined) ?? '';
      const run: ContactTimelineEvent[] = [e];
      let j = i + 1;
      while (
        j < events.length &&
        (events[j]!.type === 'dm_inbound' || events[j]!.type === 'dm_outbound') &&
        ((events[j]!.metadata?.['channelId'] as number | undefined) ?? -1) === channelId
      ) {
        run.push(events[j]!);
        j++;
      }
      items.push({ kind: 'dmBlock', channelId, platform, runs: run });
      i = j;
    } else {
      items.push({ kind: 'event', event: e });
      i++;
    }
  }
  return items;
}

function platformIconName(platform: string): IconName | null {
  if (platform === 'facebook') return 'social-facebook';
  if (platform === 'instagram') return 'social-instagram';
  return null;
}

/* Bitácora V2: cada evento del timeline es una entrada de acordeón — disco
 * del canal (relleno tenue = entrante), etiqueta fija, título, pill de
 * dirección, hora, chevron. El cuerpo expandido trae el resumen y el enlace
 * al registro de origen. Varias entradas pueden estar abiertas a la vez. */
const SOURCE_CHANNEL: Record<ContactTimelineEvent['source'], { channel: LogChannel; icon?: IconName }> = {
  email:    { channel: 'email' },
  dm:       { channel: 'sms' },
  lead:     { channel: 'system', icon: 'bolt' },
  project:  { channel: 'system', icon: 'projects' },
  contact:  { channel: 'note' },
  estimate: { channel: 'system', icon: 'billing' },
  invoice:  { channel: 'system', icon: 'invoice' },
};

function EventRow({ event }: { event: ContactTimelineEvent }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const direction = TIMELINE_DIRECTION[event.type] ?? 'event';
  const meta = SOURCE_CHANNEL[event.source] ?? { channel: 'system' as LogChannel };
  const dmPlatform = event.source === 'dm' ? String(event.metadata?.['platform'] ?? '') : '';
  const channel: LogChannel = dmPlatform === 'instagram' ? 'instagram' : dmPlatform === 'whatsapp' ? 'whatsapp' : meta.channel;
  const d = new Date(event.timestamp);
  return (
    <LogEntry
      channel={channel}
      {...(meta.icon !== undefined && dmPlatform === '' ? { icon: meta.icon } : {})}
      channelLabel={t(`contacts.outreach.source.${event.source}`, { defaultValue: event.source })}
      direction={direction === 'received' ? 'in' : 'out'}
      title={event.title}
      pill={
        <Pill tone={DIRECTION_TONES[direction]} variant="outline" size="xs">
          {t(`contacts.outreach.direction.${direction}`)}
        </Pill>
      }
      time={d.toLocaleDateString('es', { day: 'numeric', month: 'short' })}
      open={open}
      onToggle={() => setOpen(v => !v)}
    >
      {event.summary && (
        <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', fontFamily: 'var(--evo-font)', lineHeight: 1.7, color: 'var(--evo-fg-2)' }}>
          {event.summary}
        </p>
      )}
      <LogKV label={t('contacts.outreach.when', { defaultValue: 'Fecha' })} value={d.toLocaleString()} />
      {event.deepLink && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
          <Button variant="outline" tone="muted" size="sm" onClick={() => navigate(event.deepLink!)}>
            {t('contacts.outreach.openSource', { defaultValue: 'Abrir registro' })}
          </Button>
        </div>
      )}
    </LogEntry>
  );
}

function DmConversationBlock({ contactId, channelId, platform, runs, channel }: {
  contactId: number;
  channelId: number;
  platform: string;
  runs: ContactTimelineEvent[];
  channel: DmReplyChannel | undefined;
}) {
  const { t } = useTranslation();
  const iconName = platformIconName(platform);
  // Del más viejo al más nuevo adentro del bloque — se lee como una
  // conversación real, aunque el timeline general esté ordenado al revés.
  const ordered = [...runs].reverse();
  // Plegado por defecto: una conversación de Instagram con veinte mensajes se
  // comía media pantalla y empujaba el resto del historial fuera de la vista.
  // Cerrado se ve de qué canal es, cuántos mensajes tiene y el último —
  // suficiente para decidir si abrirlo.
  const [abierto, setAbierto] = useState(false);
  const ultimo = ordered[ordered.length - 1];
  const resumen = ultimo
    ? (ultimo.summary ?? '').slice(0, 90)
    : '';

  return (
    <div style={{ margin: '0 var(--evo-log-inset)', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)', background: 'var(--evo-bg-2)', overflow: 'hidden' }}>
      <div
        onClick={() => setAbierto(v => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderBottom: abierto ? '1px solid var(--evo-stroke)' : 'none', cursor: 'pointer', userSelect: 'none' }}
      >
        {iconName ? <Icon name={iconName} size={14} /> : <span style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)' }}>{platform || '—'}</span>}
        <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', fontWeight: 500, flexShrink: 0 }}>
          {t(`contacts.outreach.platform.${platform}`, { defaultValue: platform })}
        </span>
        <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', whiteSpace: 'nowrap' }}>
          {t('contacts.outreach.messageCount', { count: ordered.length })}
        </span>
        {/* Plegado, el último mensaje a la vista: es lo que dice si hace falta
            abrir. */}
        {!abierto && resumen && (
          <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {resumen}
          </span>
        )}
        <span style={{ flex: abierto ? 1 : 0 }} />
        <span style={{ flexShrink: 0, display: 'inline-flex', color: 'var(--evo-fg-4)', transform: abierto ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <Icon name="chevron-down" size={12} />
        </span>
      </div>
      {abierto && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px' }}>
        {ordered.map(e => {
          const isInbound = e.type === 'dm_inbound';
          return (
            <div key={e.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
              <Pill tone={isInbound ? 'success' : 'cyan'} variant="subtle" size="xs">
                {t(`contacts.outreach.direction.${isInbound ? 'received' : 'sent'}`)}
              </Pill>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)' }}>
                  {e.summary}
                </span>
                <span style={{ display: 'block', fontSize: '10px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
                  {new Date(e.timestamp).toLocaleString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      )}
      {abierto && channel && (
        <div style={{ padding: '10px', borderTop: '1px solid var(--evo-stroke)' }}>
          <DmReplyComposer contactId={contactId} channel={channel} />
        </div>
      )}
    </div>
  );
}

export function ContactOutreachTab({ contactId }: { contactId: number }) {
  const { t } = useTranslation();
  const { data, isLoading } = useContactTimeline(contactId);
  const { data: replyData } = useDmReplyChannels(contactId);
  const events = data?.events ?? [];
  const items = groupTimeline(events);
  const composerShownForChannel = new Set<number>();

  // Eventos ocultos por módulo apagado. Se dice SIEMPRE que haya alguno —
  // decisión explícita de Eduardo (2026-08-17): un cliente que ve desaparecer
  // su historia sin explicación asume que perdió datos. Los eventos siguen en
  // la DB; lo único que cambió es que dejaron de leerse.
  const hiddenCount = Object.values(data?.hiddenByModule ?? {})
    .reduce<number>((sum, n) => sum + (n ?? 0), 0);

  // Mismo marco que el Outreach de Leads y Proyectos: paddingTop +
  // DetailSection con título — el contenido difiere (acá es el timeline
  // transversal del contacto), el layout no.
  return (
    <div style={{ paddingTop: 'var(--evo-space-6)' }}>
    <DetailSection title={t('contacts.outreach.title')} divider={false}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
      {isLoading && <LoadingState size="sm" />}
      {!isLoading && events.length === 0 && <EmptyState title={t('contacts.outreach.empty')} />}
      {!isLoading && hiddenCount > 0 && (
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
          {t('contacts.outreach.hiddenByModule', { count: hiddenCount })}
        </span>
      )}
      {!isLoading && events.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-log-gap)', margin: '0 calc(-1 * var(--evo-log-inset))' }}>
          {items.map((item, idx) => {
            if (item.kind === 'event') return <EventRow key={item.event.id} event={item.event} />;

            // Solo el bloque más reciente de cada canal (orden DESC = el
            // primero que aparece) lleva el composer — los bloques más
            // viejos del mismo canal son historia, no el hilo activo.
            const showComposer = !composerShownForChannel.has(item.channelId);
            if (showComposer) composerShownForChannel.add(item.channelId);
            const channel = showComposer ? replyData?.channels.find(c => c.channelId === item.channelId) : undefined;

            return (
              <DmConversationBlock
                key={`dm-block-${idx}-${item.channelId}`}
                contactId={contactId}
                channelId={item.channelId}
                platform={item.platform}
                runs={item.runs}
                channel={channel}
              />
            );
          })}
        </div>
      )}
    </div>
    </DetailSection>
    </div>
  );
}

export function ContactDetail({ id, onClose, onDeleted }: Props) {
  const { t } = useTranslation();
  const { data: contact, isLoading } = useContact(id);
  const { data: briefCoreData } = useBriefCore(id);
  const briefCore = briefCoreData?.core ?? null;
  const updateBriefCore = useUpdateBriefCore(id);
  const { data: contactCategories = [] } = useContactCategoriesList();
  const createCategory = useCreateContactCategory();
  const { data: assignedTags = [] } = useContactTagAssignments(id);
  const { data: allTags = [] } = useContactTagsList();
  const assignTag = useAssignTag();
  const removeTag = useRemoveTag();
  const updateContact = useUpdateContact();
  const deleteContact = useDeleteContact();
  const mergeContacts = useMergeContacts();
  const propagation = usePropagation();

  const { data: emails = [] } = useContactEmails(id);
  const { data: phones = [] } = useContactPhones(id);
  const addEmail = useAddContactEmail();
  const removeEmail = useRemoveContactEmail();
  const setPrimaryEmail = useSetPrimaryContactEmail();
  const addPhone = useAddContactPhone();
  const removePhone = useRemoveContactPhone();
  const setPrimaryPhone = useSetPrimaryContactPhone();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<Contact>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [pendingPropCat, setPendingPropCat] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [newCatInput, setNewCatInput] = useState('');
  const [showNewCatInput, setShowNewCatInput] = useState(false);
  // La pestaña vive en la URL: volver con el navegador cae donde estabas.
  const [activeTab, setActiveTab] = useTabParam(['info', 'outreach', 'related'] as const, 'info');
  const [newEmail, setNewEmail] = useState('');
  const [newEmailLabel, setNewEmailLabel] = useState('work');
  const [emailAddError, setEmailAddError] = useState<string | null>(null);
  const [newPhone, setNewPhone] = useState('');
  // Notas editables en la vista (no solo en el modo formulario): borrador
  // local, se guarda explícito. null = sin editar.
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const [newPhoneLabel, setNewPhoneLabel] = useState('mobile');
  const [phoneAddError, setPhoneAddError] = useState<string | null>(null);

  const startEdit = () => {
    if (!contact) return;
    setForm({
      firstName: contact.firstName ?? '',
      lastName: contact.lastName ?? '',
      businessName: contact.businessName ?? '',
      address: contact.address ?? '',
      website: contact.website ?? '',
      category: contact.category,
      notes: contact.notes ?? '',
    });
    setNewEmail('');
    setNewEmailLabel('work');
    setEmailAddError(null);
    setNewPhone('');
    setNewPhoneLabel('mobile');
    setPhoneAddError(null);
    setEditing(true);
  };

  async function handleAddEmail(): Promise<boolean> {
    if (!newEmail.trim()) return true;
    setEmailAddError(null);
    try {
      await addEmail.mutateAsync({ contactId: id, email: newEmail.trim(), label: newEmailLabel });
      setNewEmail('');
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setEmailAddError(msg === 'email_taken' ? t('contacts.emails.duplicateError') : (msg || t('common.error')));
      return false;
    }
  }

  async function handleAddPhone(): Promise<boolean> {
    if (!newPhone.trim()) return true;
    setPhoneAddError(null);
    try {
      await addPhone.mutateAsync({ contactId: id, phone: newPhone.trim(), label: newPhoneLabel });
      setNewPhone('');
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setPhoneAddError(msg || t('common.error'));
      return false;
    }
  }

  const handleSave = async () => {
    // Lo tecleado en 'agregar correo/teléfono' sin presionar Agregar también
    // se guarda: 'escribí y le di a Guardar' es el gesto natural y se perdía.
    // handleAdd* limpia el input al lograrlo — si quedó texto, falló (correo
    // duplicado/ inválido): el formulario se queda abierto mostrando el error.
    if (!(await handleAddEmail())) return;
    if (!(await handleAddPhone())) return;
    await updateContact.mutateAsync({ id, ...form });
    if (form.category && form.category !== contact?.category && contact?.primaryEmail) {
      setPendingPropCat(form.category);
    }
    setEditing(false);
  };

  const { confirm, dialog: confirmDialog } = useConfirm();
  const { setOverride } = useTopBarCtx();

  const handleDelete = async () => {
    await deleteContact.mutateAsync(id);
    onDeleted();
  };

  const contactName = contact
    ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.businessName || t('contacts.detail')
    : t('contacts.detail');

  // V2: el título de página lleva el nombre (una sola vez) y las ACCIONES
  // viven en el encabezado como menú — no en la barra del detalle.
  useEffect(() => {
    setOverride({
      title: contactName,
      bottomActions: (
        <PillGroup size="md">
          <ActionDropdown
            label={t('leads.actionsMenu')}
            tone="cyan"
            items={[
              { type: 'action', label: t('common.edit'), onClick: () => { setActiveTab('info'); startEdit(); } },
              { type: 'action', label: t('contacts.merge'), onClick: () => { setActiveTab('info'); setShowMerge(true); } },
              { type: 'separator' },
              {
                type: 'action',
                label: t('common.delete'),
                onClick: () => {
                  void (async () => {
                    const ok = await confirm({
                      title: t('contacts.deleteSingleConfirm'),
                      confirmLabel: t('common.delete'),
                      cancelLabel: t('common.cancel'),
                      tone: 'danger',
                    });
                    if (ok) await handleDelete();
                  })();
                },
              },
            ]}
          />
        </PillGroup>
      ),
    });
    return () => setOverride({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactName, t]);

  const handleMerge = async (targetId: number) => {
    try {
      await mergeContacts.mutateAsync({ keepId: id, mergeId: targetId });
      setShowMerge(false);
    } catch {
      // MergeContactSelector handles UI; silent here
    }
  };

  const unassignedTags = allTags.filter(tg => !assignedTags.some(a => a.id === tg.id));
  const filteredUnassigned = tagInput
    ? unassignedTags.filter(tg => tg.label.toLowerCase().includes(tagInput.toLowerCase()))
    : unassignedTags;

  return (
    <div className="flex flex-col h-full">
      {/* Barra del detalle V2: Volver · separador · pestañas centradas ·
          pills de estado. Las acciones viven en el encabezado de página; el
          nombre va SOLO en el título de página. padding 0 --evo-page-pad-x:
          mismo riel que las tarjetas de abajo (regla del 2026-08-29). */}
      <div
        className="shrink-0"
        style={{
          height: 'var(--evo-subnav-h)',
          padding: '0 var(--evo-page-pad-x)',
          borderBottom: '1px solid var(--evo-stroke)',
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, paddingRight: '16px' }}>
          <Button
            variant="link"
            tone="muted"
            size="sm"
            icon={<Icon name="chevron-left" size={14} />}
            onClick={onClose}
          >
            {t('detail.back')}
          </Button>
          <div style={{ width: '1px', height: 'var(--evo-divider-h)', background: 'var(--evo-stroke)', flexShrink: 0, alignSelf: 'center' }} />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', overflow: 'hidden', minWidth: 0 }}>
          <Tabs<'info' | 'outreach' | 'related'>
            tabs={[
              { value: 'info',     label: t('detail.tabs.info') },
              { value: 'outreach', label: t('detail.tabs.outreach') },
              { value: 'related',  label: t('detail.tabs.related') },
            ]}
            value={activeTab}
            onChange={v => setActiveTab(v)}
            size="sm"
            noBorder
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexShrink: 0, paddingLeft: 'var(--evo-space-4)' }}>
          {contact && (
            <>
              <Pill size="xs" tone="muted">{t(`contacts.source_${contact.source}`, { defaultValue: contact.source })}</Pill>
              <Pill size="xs" tone={getContactCategoryTone(contact.category)}>
                {t(`contacts.category.${contact.category}`, { defaultValue: contact.category })}
              </Pill>
            </>
          )}
        </div>
      </div>

      {confirmDialog}

      <div className="flex-1 overflow-y-auto" style={{ padding: 'var(--evo-space-5) var(--evo-page-pad-x)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)', alignContent: 'flex-start' }}>
        {/* Relacionados (V2): una tarjeta por módulo ligado — no hay pestaña por módulo */}
        {/* Relacionados: rejilla compartida de 5 módulos (RelatedModulesGrid),
            la MISMA estructura que Prospectos y Proyectos. */}
        {activeTab === 'related' && (
          <RelatedModulesGrid contactId={id} contactName={contactName} />
        )}
        {activeTab === 'outreach' && (
          <ContactOutreachTab contactId={id} />
        )}
        {activeTab === 'info' && isLoading && (
          <LoadingState size="sm" fullHeight />
        )}
        {activeTab === 'info' && contact && (
          <div style={{ marginTop: 'var(--evo-space-4)' }}>
            <ContactProfileView
              contactId={id}
              contactName={
                [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
                contact.businessName ||
                contact.primaryEmail ||
                String(id)
              }
            />
          </div>
        )}
        {activeTab === 'info' && contact && !editing && (
          <>
            {/* Fila 1 (V2): Datos del contacto | Relación — dos tarjetas
                parejas en vez de una tira de 1200px. */}
            <div className="evo-duo-grid">
            <DetailSection
              title={t('contacts.tab.data')}
              divider={false}
              titleRight={
                <SectionHeaderAction onClick={startEdit}>
                  {t('common.edit')}
                </SectionHeaderAction>
              }
            >
              <KVGrid>
                {/* Cada campo se edita en su fila (lápiz al hover) — no hace
                    falta entrar al modo formulario para tocar uno. */}
                <KVRow label={t('contacts.firstName')} value={contact.firstName ?? ''} onSave={async v => { await updateContact.mutateAsync({ id, firstName: v }); }} />
                <KVRow label={t('contacts.lastName')} value={contact.lastName ?? ''} onSave={async v => { await updateContact.mutateAsync({ id, lastName: v }); }} />
                <KVRow label={t('contacts.businessName')} value={contact.businessName ?? ''} onSave={async v => { await updateContact.mutateAsync({ id, businessName: v }); }} />
                <KVRow label={t('contacts.address')} value={contact.address ?? ''} onSave={async v => { await updateContact.mutateAsync({ id, address: v }); }} />
                <KVRow
                  label={t('contacts.website')}
                  value={contact.website ?? ''}
                  type="url"
                  renderValue={v => (
                    <a href={/^https?:\/\//.test(v) ? v : `https://${v}`} target="_blank" rel="noreferrer" style={{ color: 'var(--evo-cyan)', textDecoration: 'none' }}>
                      {v}
                    </a>
                  )}
                  onSave={async v => { await updateContact.mutateAsync({ id, website: v }); }}
                />
                <KVRow
                  label={t('contacts.email')}
                  value={emails.length === 0 ? undefined : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
                      {emails.map(e => (
                        <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-1)', flexWrap: 'wrap' }}>
                          <span>{e.email}</span>
                          <Pill size="xs" tone="muted" variant="subtle">{t(`contacts.emails.label.${e.label}`, { defaultValue: e.label })}</Pill>
                          {e.isPrimary === 1 && <Pill size="xs" tone="cyan" variant="subtle">{t('contacts.emails.primary')}</Pill>}
                        </div>
                      ))}
                    </div>
                  )}
                />
                <KVRow
                  label={t('contacts.phone')}
                  value={phones.length === 0 ? undefined : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
                      {phones.map(p => (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-1)', flexWrap: 'wrap' }}>
                          <span>{p.phone}</span>
                          <Pill size="xs" tone="muted" variant="subtle">{t(`contacts.phones.label.${p.label}`, { defaultValue: p.label })}</Pill>
                          {p.isPrimary === 1 && <Pill size="xs" tone="cyan" variant="subtle">{t('contacts.phones.primary')}</Pill>}
                        </div>
                      ))}
                    </div>
                  )}
                />
              </KVGrid>
              {/* Redes sociales — mismos iconos que LEADS (primitive
                  compartido): cian = abre el perfil, gris = agregar URL. */}
              <div style={{ marginTop: 'var(--evo-space-3)' }}>
                <SocialLinksRow
                  values={{
                    socialFacebook: contact.socialFacebook,
                    socialInstagram: contact.socialInstagram,
                    socialTwitter: contact.socialTwitter,
                    socialLinkedin: contact.socialLinkedin,
                    socialYoutube: contact.socialYoutube,
                    socialTiktok: contact.socialTiktok,
                  }}
                  saving={updateContact.isPending}
                  onSave={async (key, url) => { await updateContact.mutateAsync({ id, [key]: url }); }}
                />
              </div>
            </DetailSection>
            <DetailSection title={t('contacts.relationship')} divider={false}>
              <KVGrid>
                <KVRow
                  label={t('contacts.relationship')}
                  value={
                    <Select<string>
                      size="sm"
                      value={contact.category}
                      options={contactCategories.map(c => ({ value: c.name, label: t(`contacts.category.${c.name}`, { defaultValue: c.name }) }))}
                      onChange={v => {
                        if (!v || v === contact.category) return;
                        void updateContact.mutateAsync({ id, category: v }).then(() => {
                          // Misma propagación que el formulario: ofrecer
                          // aplicar la categoría a los correos del contacto.
                          if (contact.primaryEmail) setPendingPropCat(v);
                        });
                      }}
                    />
                  }
                />
                <KVRow
                  label={t('contacts.columns.source', { defaultValue: 'Origen' })}
                  value={<Pill size="xs" tone="muted">{t(`contacts.source_${contact.source}`, { defaultValue: contact.source })}</Pill>}
                />
                <KVRow label={t('contacts.interactions_label')} value={String(contact.interactionCount)} />
                <KVRow label={t('contacts.firstSeen')} value={parseServerDate(contact.firstSeen).toLocaleDateString()} last />
              </KVGrid>
            </DetailSection>
            </div>

            <div className="evo-duo-grid">
            <DetailSection
              title={t('contacts.notes_label')}
              titleRight={notesDraft === null ? (
                <SectionHeaderAction onClick={() => setNotesDraft(contact.notes ?? '')}>
                  {t('common.edit')}
                </SectionHeaderAction>
              ) : undefined}
            >
              {notesDraft === null ? (
                <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: contact.notes ? 'var(--evo-fg-2)' : 'var(--evo-fg-4)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {contact.notes || '—'}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
                  <Textarea rows={3} fullWidth resize="vertical" value={notesDraft} onChange={setNotesDraft} autoFocus />
                  <div style={{ display: 'flex', gap: 'var(--evo-space-2)' }}>
                    <Button size="sm" tone="cyan" variant="solid" loading={updateContact.isPending}
                      onClick={() => { void updateContact.mutateAsync({ id, notes: notesDraft }).then(() => setNotesDraft(null)); }}>
                      {t('common.save')}
                    </Button>
                    <Button size="sm" tone="muted" variant="outline" onClick={() => setNotesDraft(null)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </DetailSection>

            <DetailSection title={t('contacts.tag.assigned')}>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {assignedTags.length === 0 && (
                  <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>{t('contacts.tag.none')}</span>
                )}
                {assignedTags.map(tag => (
                  <Chip
                    key={tag.id}
                    label={tag.label}
                    {...(tag.color != null ? { color: tag.color } : {})}
                    onRemove={() => removeTag.mutate({ contactId: id, tagId: tag.id })}
                  />
                ))}
              </div>
              <div className="relative">
                <Input
                  type="text"
                  size="sm"
                  fullWidth
                  placeholder={t('contacts.tag.add')}
                  value={tagInput}
                  onChange={v => { setTagInput(v); setShowTagDropdown(true); }}
                  onFocus={() => setShowTagDropdown(true)}
                  onBlur={() => setTimeout(() => setShowTagDropdown(false), 150)}
                />
                {showTagDropdown && filteredUnassigned.length > 0 && (
                  <div
                    className="absolute left-0 right-0 z-10 rounded overflow-hidden"
                    style={{ top: '100%', background: 'var(--evo-bg-3)', border: '1px solid var(--evo-stroke)', maxHeight: 160, overflowY: 'auto' }}
                  >
                    {filteredUnassigned.map(tag => (
                      <Pressable
                        key={tag.id}
                        fullWidth
                        onPress={() => {
                          assignTag.mutate({ contactId: id, tagId: tag.id });
                          setTagInput('');
                          setShowTagDropdown(false);
                        }}
                      >
                        <div className="text-left text-xs px-2 py-1.5" style={{ color: 'var(--evo-fg-2)' }}>
                          {tag.label}
                        </div>
                      </Pressable>
                    ))}
                  </div>
                )}
              </div>
            </DetailSection>
            </div>

            {/* Núcleo del brief — los 8 CORE_BRIEF_FIELDS que heredan todos
                los proyectos del cliente. Acá se editan sin aviso: esta ES la
                Cuenta, la fuente de verdad. */}
            {briefCore != null && briefCore.approvedAt != null && (
              <DetailSection
                title={t('contacts.briefCore.title')}
                titleRight={
                  briefCore.sourceProjectId != null ? (
                    <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
                      {t('contacts.briefCore.origin')}: {t('contacts.briefCore.originProject', { id: briefCore.sourceProjectId })}
                    </span>
                  ) : undefined
                }
              >
                <KVGrid columns={2}>
                  <KVRow label={t('projects.brief.businessName')} value={briefCore.businessName ?? ''} onSave={async v => { await updateBriefCore.mutateAsync({ businessName: v }); }} />
                  <KVRow label={t('projects.brief.industry')} value={briefCore.industry ?? ''} onSave={async v => { await updateBriefCore.mutateAsync({ industry: v }); }} />
                  <KVRow label={t('projects.brief.audience')} value={briefCore.audience ?? ''} onSave={async v => { await updateBriefCore.mutateAsync({ audience: v }); }} />
                  <KVRow label={t('projects.brief.valueProposition')} value={briefCore.valueProposition ?? ''} onSave={async v => { await updateBriefCore.mutateAsync({ valueProposition: v }); }} />
                  <KVRow label={t('projects.brief.tone')} value={briefCore.tone ?? ''} onSave={async v => { await updateBriefCore.mutateAsync({ tone: v }); }} />
                  <KVRow label={t('projects.brief.typography')} value={briefCore.typography ?? ''} onSave={async v => { await updateBriefCore.mutateAsync({ typography: v }); }} />
                  <KVRow label={t('projects.brief.colors.label')} value={briefCore.colors?.text ?? ''} onSave={async v => { await updateBriefCore.mutateAsync({ colors: { ...(briefCore.colors ?? {}), text: v } }); }} />
                  <KVRow label={t('projects.brief.logo')} value={briefCore.logo ? (briefCore.logo.split('/').pop() ?? briefCore.logo) : ''} last />
                </KVGrid>
              </DetailSection>
            )}

            {showMerge && (
              <MergeContactSelector
                currentContactId={id}
                currentContactName={
                  [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
                  contact.businessName ||
                  contact.primaryEmail ||
                  String(id)
                }
                onMergeConfirmed={(targetId) => void handleMerge(targetId)}
                onCancel={() => setShowMerge(false)}
                isMerging={mergeContacts.isPending}
              />
            )}
          </>
        )}

        {activeTab === 'info' && contact && editing && (
          <form
            className="flex flex-col gap-3"
            onSubmit={e => { e.preventDefault(); void handleSave(); }}
          >
            {[
              { key: 'firstName', label: t('contacts.firstName') },
              { key: 'lastName', label: t('contacts.lastName') },
              { key: 'businessName', label: t('contacts.businessName') },
              { key: 'address', label: t('contacts.address') },
              { key: 'website', label: t('contacts.website') },
            ].map(({ key, label }) => (
              <Input
                key={key}
                label={label}
                size="sm"
                fullWidth
                value={(form as Record<string, string>)[key] ?? ''}
                onChange={v => setForm(f => ({ ...f, [key]: v }))}
              />
            ))}

            {/* Multi-value emails */}
            <div>
              <label className="block text-xs mb-1.5" style={{ color: 'var(--evo-fg-3)' }}>{t('contacts.email')}</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
                {emails.map(e => (
                  <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-1)' }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.email}</span>
                    <Pill size="xs" tone="muted" variant="subtle">{t(`contacts.emails.label.${e.label}`, { defaultValue: e.label })}</Pill>
                    {e.isPrimary === 1
                      ? <Pill size="xs" tone="cyan" variant="subtle">{t('contacts.emails.primary')}</Pill>
                      : <IconButton
                          icon={<Icon name="ok" size={12} />}
                          ariaLabel={t('contacts.emails.setPrimary')}
                          variant="ghost" tone="muted" size="sm"
                          disabled={setPrimaryEmail.isPending}
                          onClick={() => setPrimaryEmail.mutate({ contactId: id, emailId: e.id })}
                        />
                    }
                    <IconButton
                      icon={<Icon name="close" size={11} />}
                      ariaLabel={t('contacts.emails.remove')}
                      variant="ghost" tone="danger" size="sm"
                      disabled={removeEmail.isPending}
                      onClick={() => removeEmail.mutate({ contactId: id, emailId: e.id })}
                    />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 'var(--evo-space-1)', alignItems: 'flex-start', marginTop: emails.length > 0 ? 'var(--evo-space-1)' : undefined }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      type="email"
                      size="sm"
                      fullWidth
                      placeholder={t('contacts.emails.placeholder')}
                      value={newEmail}
                      onChange={v => { setNewEmail(v); setEmailAddError(null); }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void handleAddEmail(); } }}
                      {...(emailAddError ? { error: emailAddError } : {})}
                    />
                  </div>
                  <Select
                    size="sm"
                    value={newEmailLabel}
                    onChange={v => { if (v) setNewEmailLabel(v); }}
                    options={[
                      { value: 'work', label: t('contacts.emails.label.work') },
                      { value: 'personal', label: t('contacts.emails.label.personal') },
                      { value: 'other', label: t('contacts.emails.label.other') },
                    ]}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    tone="cyan"
                    icon={<Icon name="plus" size={11} />}
                    disabled={!newEmail.trim() || addEmail.isPending}
                    loading={addEmail.isPending}
                    onClick={() => { void handleAddEmail(); }}
                  >
                    {t('contacts.emails.add')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Multi-value phones */}
            <div>
              <label className="block text-xs mb-1.5" style={{ color: 'var(--evo-fg-3)' }}>{t('contacts.phone')}</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
                {phones.map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-1)' }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.phone}</span>
                    <Pill size="xs" tone="muted" variant="subtle">{t(`contacts.phones.label.${p.label}`, { defaultValue: p.label })}</Pill>
                    {p.isPrimary === 1
                      ? <Pill size="xs" tone="cyan" variant="subtle">{t('contacts.phones.primary')}</Pill>
                      : <IconButton
                          icon={<Icon name="ok" size={12} />}
                          ariaLabel={t('contacts.phones.setPrimary')}
                          variant="ghost" tone="muted" size="sm"
                          disabled={setPrimaryPhone.isPending}
                          onClick={() => setPrimaryPhone.mutate({ contactId: id, phoneId: p.id })}
                        />
                    }
                    <IconButton
                      icon={<Icon name="close" size={11} />}
                      ariaLabel={t('contacts.phones.remove')}
                      variant="ghost" tone="danger" size="sm"
                      disabled={removePhone.isPending}
                      onClick={() => removePhone.mutate({ contactId: id, phoneId: p.id })}
                    />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 'var(--evo-space-1)', alignItems: 'flex-start', marginTop: phones.length > 0 ? 'var(--evo-space-1)' : undefined }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      type="tel"
                      size="sm"
                      fullWidth
                      placeholder={t('contacts.phones.placeholder')}
                      value={newPhone}
                      onChange={v => { setNewPhone(v); setPhoneAddError(null); }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void handleAddPhone(); } }}
                      {...(phoneAddError ? { error: phoneAddError } : {})}
                    />
                  </div>
                  <Select
                    size="sm"
                    value={newPhoneLabel}
                    onChange={v => { if (v) setNewPhoneLabel(v); }}
                    options={[
                      { value: 'mobile', label: t('contacts.phones.label.mobile') },
                      { value: 'work', label: t('contacts.phones.label.work') },
                      { value: 'home', label: t('contacts.phones.label.home') },
                      { value: 'other', label: t('contacts.phones.label.other') },
                    ]}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    tone="cyan"
                    icon={<Icon name="plus" size={11} />}
                    disabled={!newPhone.trim() || addPhone.isPending}
                    loading={addPhone.isPending}
                    onClick={() => { void handleAddPhone(); }}
                  >
                    {t('contacts.phones.add')}
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('contacts.category_label', { defaultValue: 'Category' })}</label>
              {showNewCatInput ? (
                <div className="flex gap-1">
                  <Input
                    autoFocus
                    size="sm"
                    fullWidth
                    value={newCatInput}
                    onChange={v => setNewCatInput(v)}
                    placeholder={t('contacts.category.namePlaceholder', { defaultValue: 'Category name…' })}
                    onKeyDown={e => {
                      if (e.key === 'Escape') { setShowNewCatInput(false); setNewCatInput(''); }
                    }}
                  />
                  <Button
                    type="button"
                    variant="solid"
                    tone="cyan"
                    size="sm"
                    loading={createCategory.isPending}
                    disabled={!newCatInput.trim() || createCategory.isPending}
                    onClick={() => {
                      if (!newCatInput.trim()) return;
                      createCategory.mutate(newCatInput.trim(), {
                        onSuccess: cat => {
                          setForm(f => ({ ...f, category: cat.name }));
                          setShowNewCatInput(false);
                          setNewCatInput('');
                        },
                      });
                    }}
                  >
                    {t('common.create', { defaultValue: 'Create' })}
                  </Button>
                  <Button type="button" variant="outline" tone="muted" size="sm" onClick={() => { setShowNewCatInput(false); setNewCatInput(''); }}>
                    {t('common.cancel')}
                  </Button>
                </div>
              ) : (
                <Select<string>
                  size="sm"
                  fullWidth
                  value={form.category ?? ''}
                  options={[
                    ...contactCategories.map(c => ({ value: c.name, label: c.name })),
                    { value: '__new__', label: `+ ${t('contacts.category.new', { defaultValue: 'Add category' })}` },
                  ]}
                  onChange={v => {
                    if (v === '__new__') { setShowNewCatInput(true); return; }
                    setForm(f => ({ ...f, category: v ?? '' }));
                  }}
                />
              )}
            </div>

            <Textarea
              label={t('contacts.notes_label')}
              rows={3}
              fullWidth
              resize="none"
              value={form.notes ?? ''}
              onChange={v => setForm(f => ({ ...f, notes: v }))}
            />

            <div className="flex gap-2 pt-1">
              <Button type="submit" variant="solid" tone="cyan" size="sm" loading={updateContact.isPending} disabled={updateContact.isPending}>
                {t('common.save')}
              </Button>
              <Button type="button" variant="outline" tone="muted" size="sm" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}
      </div>


      {pendingPropCat && contact?.primaryEmail && (
        <PropagationModal
          open={true}
          onClose={() => setPendingPropCat(null)}
          fromEmail={contact.primaryEmail}
          actionType="category"
          newValue={pendingPropCat}
          onThisOnly={() => setPendingPropCat(null)}
          onSender={() => {
            propagation.mutate({ fromEmail: contact.primaryEmail!, actionType: 'category', scope: 'sender', value: pendingPropCat });
            setPendingPropCat(null);
          }}
          onDomain={() => {
            propagation.mutate({ fromEmail: contact.primaryEmail!, actionType: 'category', scope: 'domain', value: pendingPropCat });
            setPendingPropCat(null);
          }}
          isPending={propagation.isPending}
        />
      )}
    </div>
  );
}
